/**
 * The dealership's settings (aging buckets, the asking-vs-AI gap scale,
 * margin floor, floor plan APR), loaded once from GET /api/settings.
 *
 * Until the answer arrives — and whenever it cannot be had — every consumer
 * reads the built-in defaults, which are exactly today's hard-coded values.
 * So the app with no override, or with no provider at all (tests, the public
 * pages), looks and behaves as it did before settings existed.
 *
 * Mounted inside the signed-in routes under the per-dealership DataProvider
 * (keyed on the workspace), so switching dealership loads that dealership's
 * settings; the active slug is a dependency too, for safety.
 *
 * Settings and data stay in step (QA P2-1, P2-2): the provider registers a
 * link with the DataProvider above it, which reads /api/settings with every
 * data load and applies a new version only together with the data computed
 * under it (see contexts/DataContext.js). The provider's own fetches go
 * through the same check, and `updating` is also true while such a switch
 * is under way.
 */

import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import apiService from '../services/apiService';
import { useWorkspace } from './WorkspaceContext';
import { useOptionalData } from './DataContext';
import { resolveSettings } from '../utils/dealershipSettings';
import logger from '../utils/logger';
import { DealershipSettingsContext, StaticDealershipSettings, useDealershipSettings } from './settingsContext';

// Readers import the hook from ./settingsContext (no API client); both work.
export { StaticDealershipSettings, useDealershipSettings };

export function DealershipSettingsProvider({ children }) {
    const { activeSlug } = useWorkspace();
    const [payload, setPayloadState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Only the newest request may land (a reload racing the first load).
    const seq = useRef(0);

    // The payload in use, readable at once by the DataProvider's sync. An
    // older version than the one shown is a stale read and never applies.
    const current = useRef(null);
    const setPayload = useCallback((next) => {
        const p = next && typeof next === 'object' ? next : null;
        const have = Number(current.current?.version);
        const got = Number(p?.version);
        if (p && Number.isFinite(have) && Number.isFinite(got) && got < have) return;
        current.current = p;
        setPayloadState(p);
    }, []);

    // The link with the DataProvider (absent in tests / previews).
    const data = useOptionalData();
    const registerLink = data?.registerSettingsLink;
    const observe = data?.observeSettings;
    const [syncing, setSyncing] = useState(false);
    useEffect(() => {
        if (!registerLink) return undefined;
        return registerLink({
            version: () => {
                const v = Number(current.current?.version);
                return current.current && Number.isFinite(v) ? v : null;
            },
            apply: (p) => { setPayload(p); setError(null); },
            setSyncing,
        });
    }, [registerLink, setPayload]);
    // A payload fetched here applies through the DataProvider's check when
    // there is one (data on screen computed under other settings first).
    const take = useCallback((p) => {
        if (observe && p && typeof p === 'object') observe(p);
        else setPayload(p);
    }, [observe, setPayload]);

    const reload = useCallback(async () => {
        seq.current += 1;
        const mine = seq.current;
        setLoading(true);
        try {
            const fetched = await apiService.getSettings();
            if (mine !== seq.current) return null;
            take(fetched && typeof fetched === 'object' ? fetched : null);
            setError(null);
            return fetched;
        } catch (err) {
            if (mine !== seq.current) return null;
            // Keep whatever was loaded before; the defaults when nothing was.
            logger.warn('Dealership settings could not be loaded; using the defaults', err);
            setError(err);
            return null;
        } finally {
            if (mine === seq.current) setLoading(false);
        }
    }, [take]);

    // After an admin saves (QA S-8): the server-side signals only change with
    // the rebuilt inventory, so the new settings are fetched now but applied
    // only once `refresh()` (the inventory reload) has completed. Until then
    // the old settings stay active and `updating` is true; if the reload
    // fails they stay, `updateError` is set and `retryUpdate()` tries again.
    const [updating, setUpdating] = useState(false);
    const [updateError, setUpdateError] = useState(null);
    const lastRefresh = useRef(null);
    const applyAfterRefresh = useCallback(async (refresh) => {
        lastRefresh.current = refresh;
        seq.current += 1;
        const mine = seq.current;
        setUpdating(true);
        setUpdateError(null);
        try {
            const [fetched, ok] = await Promise.all([apiService.getSettings(), Promise.resolve(refresh())]);
            if (mine !== seq.current) return false;
            if (ok === false) throw new Error('The inventory could not be reloaded.');
            // Normally already applied with the reloaded inventory (the
            // DataProvider's sync); an older read never overrides it.
            setPayload(fetched && typeof fetched === 'object' ? fetched : null);
            setError(null);
            return true;
        } catch (err) {
            if (mine !== seq.current) return false;
            logger.warn('New dealership settings not applied: the figures could not be updated', err);
            setUpdateError(err);
            return false;
        } finally {
            if (mine === seq.current) setUpdating(false);
        }
    }, [setPayload]);
    const retryUpdate = useCallback(
        () => (lastRefresh.current ? applyAfterRefresh(lastRefresh.current) : Promise.resolve(false)),
        [applyAfterRefresh],
    );

    // A new dealership in view: its settings, not the previous one's (the
    // new request supersedes any still in flight).
    useEffect(() => {
        reload();
    }, [reload, activeSlug]);

    const value = useMemo(() => ({
        ...resolveSettings(payload),
        loading,
        loaded: Boolean(payload),
        error,
        reload,
        updating: updating || syncing,
        updateError,
        applyAfterRefresh,
        retryUpdate,
    }), [payload, loading, error, reload, updating, syncing, updateError, applyAfterRefresh, retryUpdate]);

    return (
        <DealershipSettingsContext.Provider value={value}>
            {children}
        </DealershipSettingsContext.Provider>
    );
}

DealershipSettingsProvider.propTypes = { children: PropTypes.node };
